import React, { Suspense, useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Canvas } from '@react-three/fiber'
import { Stars } from '@react-three/drei'
import { CinematicBackground } from '../components/canvas/CinematicBackground'
import FloatingSidebar from '../components/FloatingSidebar'
import BeforeAfterSlider from '../components/BeforeAfterSlider'
import { AppWindow, ArrowDown, ArrowRight, Download, Zap, Layers, Image as ImageIcon, Cpu, Check, Star, Lock, Shield } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/SupabaseAuthContext'
import { supabase } from '../config/supabase'
import { toast } from 'sonner'
import { trackCustomEvent } from '../lib/meta-pixel'
import { telemetry } from '../services/telemetryService'

// IMAGENS (Mapeamento baseado nos arquivos encontrados)
const IMAGES = {
  dtfFactory: "/DTF-factory.png",
  directAi: "/directai-print1.png",
  mobileStep1: "/Mobile-carrosel/1-dtf factory gerando.png",
  mobileStep2: "/Mobile-carrosel/2-loading enviando para o montador.png",
  mobileStep3: "/Mobile-carrosel/3-no montador com a arte montada vendo o tamanho.png",
  mobileStep4: "/Mobile-carrosel/4-carregando no direct ai processando o pedido.png",
  mobileStep5: "/Mobile-carrosel/5-Pedido com arquivo inserido.png",
  desktop: "/desktop.jpg",
  spotWhite: "/PRINTS INTERFACE/SPOT WHITE/Interface do programa aqui temos contração branco fundo uploader pronto para uso.png",
  layout: "/Tela do montador fast montando.png",
  montadorResult: "/Montagem Montador Fast.png",
  spotWhiteResult: "/Contração de 2pixels spot white.png",
  comparisonBefore: "/slider antes.png",
  comparisonAfter: "/Slider depois.png"
}

export default function HomeLandingPage() {
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const [activeToolCount, setActiveToolCount] = useState<number | null>(null)

  useEffect(() => {
    let mounted = true

    const fetchActiveToolCount = async () => {
      try {
        const { count, error } = await supabase
          .from('software_versions')
          .select('id', { count: 'exact', head: true })
          .eq('is_active', true)
          .neq('id', 'overpixel-launcher')

        if (!error && mounted && typeof count === 'number') {
          setActiveToolCount(count)
        }
      } catch {
        // Fallback silencioso para manter a home estável mesmo sem contagem
      }
    }

    fetchActiveToolCount()
    return () => {
      mounted = false
    }
  }, [])

  const handleAccessWebLauncher = () => {
    window.open('https://app.overpixel.online', '_blank', 'noopener,noreferrer')
  }

  const handleDownloadLauncher = async () => {
    if (!isAuthenticated) {
      toast.error('Você precisa estar logado para baixar o Launcher')
      navigate('/auth/login')
      return
    }

    try {
      toast.loading('Preparando download...')
      
      // Track download attempt
      trackCustomEvent('LauncherDownloadAttempt', {
        user_id: user?.id,
        source: 'home_landing'
      })
      
      // Tentar buscar URL atualizada do Supabase
      const { data, error } = await supabase
        .from('software_versions')
        .select('download_url')
        .eq('id', 'overpixel-launcher')
        .single()

      let downloadUrl = 'https://www.dropbox.com/scl/fi/g7x6j3q8k9l2m4n5p0q1r/Overpixel-Launcher-Setup.exe?rlkey=8j9k0l1m2n3o4p5q6r7s8t9u0&dl=1'

      if (data?.download_url) {
        downloadUrl = data.download_url
        // Ajuste para Dropbox se necessário
        if (downloadUrl.includes('dropbox.com')) {
           if (downloadUrl.includes('dl=0')) downloadUrl = downloadUrl.replace('dl=0', 'dl=1')
           else if (!downloadUrl.includes('dl=')) downloadUrl += (downloadUrl.includes('?') ? '&dl=1' : '?dl=1')
        }
      } else if (error) {
        console.error('Erro ao buscar URL do launcher:', error)
      }

      toast.dismiss()
      
      const link = document.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', 'Overpixel-Launcher-Setup.exe')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      toast.success('Download iniciado!')
      
      // Track successful download start
      trackCustomEvent('LauncherDownloadStarted', {
        user_id: user?.id,
        url: downloadUrl
      })

      // Update user profile with launcher download status
      if (user?.id) {
        await supabase
          .from('users')
          .update({
            has_downloaded_launcher: true,
            launcher_downloaded_at: new Date().toISOString()
          })
          .eq('id', user.id)
      }

      // Track internal telemetry
      telemetry.track('launcher_download', {
        url: downloadUrl,
        version: 'latest'
      })

    } catch (err) {
      toast.dismiss()
      toast.error('Erro ao iniciar download. Tente novamente.')
      console.error(err)
    }
  }

  return (
    <div className="relative w-full min-h-screen bg-black text-white selection:bg-cyan-500/30">
      <FloatingSidebar />
      
      {/* BACKGROUND 3D (Fixo) */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <Canvas gl={{ antialias: false }} dpr={[1, 1.5]}>
          <Suspense fallback={null}>
            <CinematicBackground />
            <Stars radius={100} depth={50} count={3000} factor={4} saturation={0} fade speed={1} />
          </Suspense>
        </Canvas>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/60 to-black z-10" />
      </div>

      <div className="relative z-10 w-full">
        
        {/* HERO SECTION */}
        <section className="min-h-screen flex flex-col justify-center items-center text-center px-4 pt-20 pb-32 relative">
          
          {/* Efeito de Glow central atrás do texto */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-cyan-500/20 blur-[120px] rounded-full pointer-events-none" />

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto space-y-8 relative z-10"
          >
            {/* LOGO "O" + TITLE */}
            <div className="flex flex-col items-center mb-8">
               <motion.div 
                 initial={{ scale: 0.8, opacity: 0 }}
                 animate={{ scale: 1, opacity: 1 }}
                 transition={{ duration: 1, type: "spring" }}
                 className="relative mb-6"
               >
                  <div className="absolute inset-0 bg-cyan-500 blur-2xl opacity-40 animate-pulse" />
                  <img 
                    src="/Logo da overpixel sem fundo.png" 
                    alt="Overpixel Logo" 
                    className="w-32 h-32 md:w-48 md:h-48 object-contain relative z-10 drop-shadow-[0_0_15px_rgba(6,182,212,0.5)]" 
                  />
               </motion.div>
               
               <h1 className="text-6xl md:text-8xl font-black tracking-tighter text-white mb-2">
                 OVERPIXEL
               </h1>
            </div>
            
            <div className="space-y-6">
              <h2 className="text-4xl md:text-6xl font-bold tracking-tight leading-tight">
                Suas estampas <br className="md:hidden" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-500 animate-gradient-x">
                  em outro nível.
                </span>
              </h2>
              
              <div className="space-y-2 max-w-3xl mx-auto">
                <p className="text-2xl md:text-3xl text-white font-medium">
                  Impressão DTF com IA.
                </p>
                <p className="text-xl text-gray-400">
                  <span className="text-cyan-400 font-bold">
                    {activeToolCount !== null ? `${activeToolCount}+ ferramentas` : 'Várias ferramentas'}
                  </span> que fazem em segundos o que você leva horas.
                </p>
                <p className="text-lg text-gray-500">
                  Halftone, Melhorar resolução, Spot White — tudo no seu PC.
                </p>
              </div>
            </div>

            {/* BUTTONS */}
            <div className="pt-10 flex flex-col items-center gap-5">
              <div className="flex flex-col md:flex-row items-center justify-center gap-6 w-full">
                <button
                  onClick={handleAccessWebLauncher}
                  className="group relative w-full md:w-auto px-8 py-5 bg-white text-black rounded-full font-black text-lg hover:bg-gray-100 transition-all shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_40px_rgba(255,255,255,0.5)] hover:-translate-y-1 flex items-center justify-center gap-3 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent -translate-x-full group-hover:animate-shimmer" />
                  <AppWindow className="w-6 h-6" />
                  <span>ACESSAR LAUNCHER WEB</span>
                </button>

                <button
                  onClick={handleDownloadLauncher}
                  className="group w-full md:w-auto px-8 py-5 bg-black/40 border border-white/20 hover:border-cyan-500/50 rounded-full font-bold text-lg text-white hover:bg-white/5 transition-all backdrop-blur-md flex items-center justify-center gap-3"
                >
                  <Download className="w-6 h-6 text-cyan-400" />
                  <span>BAIXAR LAUNCHER</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform text-cyan-400" />
                </button>
              </div>

              <button
                onClick={() => document.getElementById('tools')?.scrollIntoView({ behavior: 'smooth' })}
                className="group px-6 py-3 bg-black/30 border border-white/10 hover:border-cyan-500/40 rounded-full text-sm font-bold text-gray-200 hover:text-white hover:bg-white/5 transition-all backdrop-blur-md flex items-center justify-center gap-2"
              >
                <ArrowDown className="w-4 h-4 text-cyan-400 group-hover:translate-y-0.5 transition-transform" />
                <span>Conhecer mais sobre os apps</span>
              </button>
            </div>

            {/* STATS */}
            <div className="grid grid-cols-3 gap-4 md:gap-12 pt-16 border-t border-white/10 mt-16 max-w-4xl mx-auto">
              <div className="text-center">
                <h3 className="text-3xl md:text-5xl font-black text-white mb-1">500+</h3>
                <p className="text-gray-500 uppercase text-[10px] md:text-xs tracking-widest font-bold">estúdios usam</p>
              </div>
              <div className="text-center border-l border-r border-white/5">
                <h3 className="text-3xl md:text-5xl font-black text-white mb-1">
                  {activeToolCount !== null ? `${activeToolCount}+` : '+'}
                </h3>
                <p className="text-gray-500 uppercase text-[10px] md:text-xs tracking-widest font-bold">ferramentas de IA</p>
              </div>
              <div className="text-center">
                <h3 className="text-3xl md:text-5xl font-black text-white mb-1">100%</h3>
                <p className="text-gray-500 uppercase text-[10px] md:text-xs tracking-widest font-bold">roda no seu PC</p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* TOOLS SHOWCASE */}
        <div id="tools" className="space-y-32 pb-32 px-4 md:px-12 max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Ferramentas que fazem a diferença</h2>
            <p className="text-gray-400 text-lg">Cada uma resolve um problema real do seu dia a dia.</p>
          </div>

          {/* 1. DIRECT AI */}
          <ToolSection 
            title="Direct AI"
            subtitle="GESTÃO DTF"
            description="Sistema completo de gestão de pedidos DTF: ERP, logística e produção em um só lugar. Gere no DTF Factory, monte no Montador e envie o pedido para o Direct AI."
            features={["ERP + logística", "Gestão de pedidos", "Integração Montador", "Integração DTF Factory"]}
            image={IMAGES.directAi}
            color="purple"
            align="right"
            link="/direct-ai"
          />

          {/* 2. DTF FACTORY */}
          <ToolSection 
            title="DTF Factory"
            subtitle="GERADOR IA"
            description="Crie estampas exclusivas prontas pra impressão DTF. A IA gera a arte, remove o fundo e entrega em alta definição — pronta pro seu fluxo de produção."
            features={["Arte pronta p/ DTF", "Fundo transparente", "Alta definição", "20 tokens/img"]}
            image={IMAGES.dtfFactory}
            color="cyan"
            align="left"
            badge="MAIS POPULAR"
            link="/software/dtf-fabart"
          />

          {/* 4. SPOT WHITE */}
          <ToolSection 
            title="Spot White"
            subtitle="DTF ESSENCIAL"
            description="Separação de branco perfeita, automática. Contração inteligente de borda, base automática e exportação direta pro seu RIP. A camada de branco sai perfeita toda vez."
            features={["Base Automática", "Contração de Borda", "Economia de Tinta", "Export pro RIP"]}
            image={IMAGES.spotWhite}
            color="blue"
            align="right"
            link="/software/spot-white"
          />

          {/* 5. LAYOUT MONTADOR */}
          <ToolSection 
            title="Montador Rápido"
            subtitle="PRODUÇÃO"
            description="Monte layouts DTF no piloto automático. Organize suas estampas na folha de impressão de forma otimizada. O Montador calcula o melhor encaixe pra aproveitar cada centímetro do filme."
            features={["Otimização de espaço", "Drag & Drop", "Ilimitado", "Local"]}
            image={IMAGES.layout}
            color="orange"
            align="left"
            link="/software/montador-fast"
          />
        </div>

        {/* COMPARISON SLIDER */}
        <section className="py-32 px-4 bg-black/50 border-y border-white/5 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-8">Veja os resultados</h2>
            <p className="text-gray-400 mb-12 max-w-2xl mx-auto">Esse é o resultado das nossas ferramentas transformando a arte em alta qualidade e pronto para bater na camiseta preta com halftone !</p>
            
            <div className="w-full max-w-4xl mx-auto shadow-2xl shadow-cyan-900/20 rounded-2xl border border-white/10 p-1 bg-white/5 mb-20">
              <BeforeAfterSlider 
                beforeImage={IMAGES.comparisonBefore}
                afterImage={IMAGES.comparisonAfter}
                beforeLabel="ANTES 480px"
                afterLabel="DEPOIS 4096px"
              />
            </div>

            <div className="max-w-4xl mx-auto">
              <h3 className="text-2xl md:text-4xl font-bold mb-6">Montagem Automática</h3>
              <p className="text-gray-400 mb-8">O Montador Fast organiza suas estampas automaticamente para economizar filme e tempo.</p>
              <ZoomableImage 
                src={IMAGES.montadorResult} 
                alt="Resultado da Montagem Automática" 
              />
            </div>

            <div className="max-w-4xl mx-auto mt-32">
              <div className="flex flex-col md:flex-row gap-12 items-center text-left">
                <div className="flex-1 space-y-6">
                  <div className="inline-block px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded text-blue-400 text-sm font-bold">
                    SPOT WHITE
                  </div>
                  <h3 className="text-2xl md:text-4xl font-bold">Contração Inteligente</h3>
                  <p className="text-gray-400 leading-relaxed">
                    Aplica contração apenas sobre a base colorida, <strong className="text-white">preservando os detalhes finos de branco</strong>. 
                    Isso garante que não sobre borda branca na impressão DTF, mantendo a fidelidade da estampa.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-gray-300">
                      <Check className="w-4 h-4 text-blue-500" />
                      Contração de 2 pixels na base
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-300">
                      <Check className="w-4 h-4 text-blue-500" />
                      Preserva 100% dos detalhes brancos
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-300">
                      <Check className="w-4 h-4 text-blue-500" />
                      Compatível com <strong className="text-white">Flexi</strong> e <strong className="text-white">Maintop</strong>
                    </li>
                  </ul>
                </div>
                <div className="flex-1 w-full">
                  <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-blue-900/20 bg-white/5 group">
                    <img 
                      src={IMAGES.spotWhiteResult} 
                      alt="Resultado Spot White Contração" 
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* STEPS */}
        <section className="py-32 px-4 max-w-7xl mx-auto relative">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">3 passos. Mais fácil que pedir um Pix.</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              Um fluxo simples para sair da ideia ao pedido final no Direct AI, sem pular de ferramenta em ferramenta.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <StepCard 
              number="01" 
              title="Teste no Launcher Web" 
              description="Comece em segundos no celular, tablet ou PC. A maioria dos apps já está no Launcher Web, sem instalação."
            />
            <StepCard 
              number="02" 
              title="Escolha sua ferramenta" 
              description="Cada software tem assinatura individual. Desktop é exclusivo para assinantes. Sem pacote forçado."
            />
            <StepCard 
              number="03" 
              title="Produza no piloto automático" 
              description="Gere no DTF Factory, monte no Montador e envie para o Direct AI. Cloud com tokens acessíveis e fluxo integrado."
            />
          </div>
        </section>

        {/* PRICING / TOKENS */}
        <section className="py-32 px-4 bg-gradient-to-b from-gray-900 to-black border-y border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-16 items-center">
              <div className="md:w-1/2 space-y-8">
                <div className="inline-block px-3 py-1 bg-green-500/10 border border-green-500/20 rounded text-green-400 text-sm font-bold">
                  INTEGRAÇÃO MOBILE INTELIGENTE
                </div>
                <h2 className="text-4xl md:text-5xl font-black leading-tight">
                  DO APP PARA O PEDIDO, <br/>
                  <span className="text-gray-500">TUDO CONECTADO EM SEGUNDOS.</span>
                </h2>
                <p className="text-gray-400 text-lg leading-relaxed">
                  No Launcher Web você gera no DTF Factory, envia para o Montador e já anexa no Direct AI sem retrabalho. Um fluxo só, em qualquer dispositivo.
                </p>
                
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <Zap className="w-6 h-6 text-yellow-400 shrink-0" />
                    <div>
                      <h4 className="font-bold text-white">Fluxo Integrado</h4>
                      <p className="text-sm text-gray-500">Gera, monta e envia no mesmo ecossistema.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="text-xl font-bold text-blue-400 shrink-0">∞</div>
                    <div>
                      <h4 className="font-bold text-white">Teste Imediato</h4>
                      <p className="text-sm text-gray-500">Abra no celular, tablet ou PC sem instalar.</p>
                    </div>
                  </div>
                </div>

                <motion.button
                  onClick={handleAccessWebLauncher}
                  animate={{
                    boxShadow: [
                      '0 0 0px rgba(34,211,238,0.0)',
                      '0 0 25px rgba(34,211,238,0.45)',
                      '0 0 0px rgba(34,211,238,0.0)'
                    ],
                    scale: [1, 1.03, 1]
                  }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                  className="w-full sm:w-auto px-8 py-4 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black font-black text-base tracking-wide transition-colors"
                >
                  TESTAR LAUNCHER WEB AGORA
                </motion.button>
              </div>

              <div className="md:w-1/2 w-full">
                <PhoneMockup
                  frames={[
                    { key: 'step-1', src: IMAGES.mobileStep1, title: 'DTF Factory', status: 'Gerando arte sem fundo' },
                    { key: 'step-2', src: IMAGES.mobileStep2, title: 'Pipeline', status: 'Enviando para o Montador' },
                    { key: 'step-3', src: IMAGES.mobileStep3, title: 'Montador', status: 'Ajustando tamanho da arte' },
                    { key: 'step-4', src: IMAGES.mobileStep4, title: 'Direct AI', status: 'Processando o pedido' },
                    { key: 'step-5', src: IMAGES.mobileStep5, title: 'Pedidos', status: 'Arquivo anexado com sucesso' },
                  ]}
                  alt="Launcher Web Overpixel"
                  onClick={handleAccessWebLauncher}
                  urlLabel="app.overpixel.online"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-32 px-4 max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-14 items-center">
            <div className="md:w-1/2 w-full">
              <DesktopMonitorMockup src={IMAGES.desktop} alt="Overpixel Desktop" />
            </div>

            <div className="md:w-1/2 space-y-7">
              <div className="inline-block px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded text-cyan-300 text-sm font-bold">
                DESKTOP PRO
              </div>
              <h2 className="text-4xl md:text-5xl font-black leading-tight">
                MAIS PODER. <br />
                <span className="text-gray-500">MAIS PRIVACIDADE. ZERO LIMITES.</span>
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed">
                Os aplicativos de Desktop são para quem quer privacidade, velocidade e capacidade sem limites. Gere arquivos, monte e suba pedidos na sua própria loja via Desktop com nossos apps.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 text-white font-bold mb-1">
                    <Shield className="w-4 h-4 text-cyan-300" />
                    Privacidade
                  </div>
                  <div className="text-sm text-gray-500">Processos locais e controle total.</div>
                </div>
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 text-white font-bold mb-1">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    Velocidade
                  </div>
                  <div className="text-sm text-gray-500">Fluxo profissional sem travar.</div>
                </div>
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 text-white font-bold mb-1">
                    <Cpu className="w-4 h-4 text-green-400" />
                    Capacidade
                  </div>
                  <div className="text-sm text-gray-500">Recursos avançados para alta produção.</div>
                </div>
              </div>

              <div className="pt-2 text-sm text-gray-500 flex items-center gap-2">
                <Lock className="w-4 h-4 text-gray-400" />
                Desktop disponível para assinantes.
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-32 px-4 max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Quem usa, não volta atrás.</h2>
            <p className="text-gray-400">Centenas de estúdios e designers já transformaram sua produção no Web e no Desktop.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <TestimonialCard 
              text="O Melhorador Cloud salvou várias estampas que clientes mandaram em baixa resolução. A nitidez fica profissional e recupera detalhes perdidos."
              author="Ana Clara"
              role="Designer Gráfico"
              initials="AC"
            />
            <TestimonialCard 
              text="Desde que comecei a usar o DTF Factory, meu catálogo triplicou. Consigo criar estampas exclusivas sem depender de ninguém."
              author="Rafael Mendes"
              role="Dono de Estamparia"
              initials="RM"
            />
            <TestimonialCard 
              text="O Spot White economizou uns 30% de tinta branca pra gente. A contração automática é perfeita, nunca mais tive problema de borda."
              author="Juliana Souza"
              role="Produção DTF"
              initials="JS"
            />
          </div>
        </section>

        {/* FOOTER CTA */}
        <section className="py-32 px-4 text-center bg-gradient-to-t from-cyan-900/20 to-transparent">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-4xl md:text-6xl font-black">PRONTO PRA COMEÇAR?</h2>
            <p className="text-xl text-gray-400">Teste agora no Launcher Web e evolua para o Desktop quando quiser recursos avançados de assinante.</p>
            
            <button 
              onClick={handleAccessWebLauncher}
              className="px-10 py-5 bg-white text-black rounded-full font-bold text-xl hover:bg-gray-200 transition-colors shadow-[0_0_50px_rgba(255,255,255,0.2)]"
            >
              ACESSAR LAUNCHER WEB AGORA
            </button>
            <p className="text-xs text-gray-500 uppercase tracking-widest">WEB: CELULAR/TABLET/PC • DESKTOP PRO: ASSINANTES</p>
          </div>
        </section>

        <footer className="py-12 border-t border-white/5 text-center text-gray-600">
          <p>© 2025 Overpixel. Todos os direitos reservados.</p>
        </footer>

      </div>
    </div>
  )
}

function ZoomableImage({ src, alt }: { src: string, alt: string }) {
  const [isHovered, setIsHovered] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - left) / width) * 100
    const y = ((e.clientY - top) / height) * 100
    setPosition({ x, y })
  }

  return (
    <div 
      className="relative w-full overflow-hidden rounded-2xl border border-white/10 shadow-2xl shadow-orange-900/20 bg-white/5 cursor-crosshair"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseMove={handleMouseMove}
    >
      {/* Static Image (Visible when not hovered) */}
      <img 
        src={src} 
        alt={alt} 
        className={`w-full h-auto object-cover transition-opacity duration-200 ${isHovered ? 'opacity-0' : 'opacity-100'}`}
      />

      {/* Zoomed Background (Visible when hovered) */}
      <div 
        className={`absolute inset-0 w-full h-full bg-no-repeat transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
        style={{
          backgroundImage: `url('${src}')`,
          backgroundSize: '500%', // 5x zoom
          backgroundPosition: `${position.x}% ${position.y}%`
        }}
      />
      
      <div className={`absolute bottom-4 right-4 bg-black/60 backdrop-blur px-3 py-1 rounded-full text-xs text-white/80 pointer-events-none transition-opacity duration-300 ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
        Passe o mouse para zoom 5x
      </div>
    </div>
  )
}

// --- COMPONENTS ---

function ToolSection({ title, subtitle, description, features, image, color, align, badge, link }: any) {
  const isLeft = align === 'left'
  const navigate = useNavigate()
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      className={`flex flex-col ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'} gap-12 items-center`}
    >
      <div className="flex-1 space-y-6">
        <div className="flex items-center gap-4">
          <span className={`text-${color}-400 font-bold tracking-wider text-sm uppercase`}>{subtitle}</span>
          {badge && <span className="bg-yellow-500/20 text-yellow-300 text-xs font-bold px-2 py-1 rounded">{badge}</span>}
        </div>
        
        <h3 className="text-4xl md:text-5xl font-bold">{title}</h3>
        <p className="text-gray-400 text-lg leading-relaxed">{description}</p>
        
        <ul className="grid grid-cols-2 gap-4 pt-4">
          {features.map((feature: string, i: number) => (
            <li key={i} className="flex items-center gap-2 text-sm font-medium text-gray-300">
              <div className={`w-1.5 h-1.5 rounded-full bg-${color}-500`} />
              {feature}
            </li>
          ))}
        </ul>

        <button 
          onClick={() => link && navigate(link)}
          className={`mt-8 px-6 py-3 border border-${color}-500/30 hover:bg-${color}-500/10 text-${color}-400 rounded-lg font-bold transition-all flex items-center gap-2`}
        >
          CONHECER <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 w-full">
        <div className={`relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-${color}-900/20 bg-white/5 group`}>
          <div className={`absolute inset-0 bg-gradient-to-br from-${color}-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity`} />
          <img src={image} alt={title} className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700" />
        </div>
      </div>
    </motion.div>
  )
}

function StepCard({ number, title, description }: any) {
  return (
    <div className="relative p-7 rounded-2xl bg-black/45 border border-white/15 hover:border-cyan-400/30 hover:bg-black/55 transition-colors min-h-[220px]">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/35 to-transparent" />
      <div className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-5">
        <span className="text-3xl md:text-4xl font-black text-white/25 leading-none">{number}</span>
      </div>
      <h3 className="text-xl font-bold text-white mb-3 leading-tight">{title}</h3>
      <p className="text-gray-300/90 leading-relaxed text-[15px]">{description}</p>
    </div>
  )
}

function PricingItem({ icon, title, subtitle, cost, highlight }: any) {
  return (
    <div className={`flex items-center justify-between p-4 rounded-xl ${highlight ? 'bg-white/10 border border-white/20' : 'bg-black/20'}`}>
      <div className="flex items-center gap-4">
        <div className="p-2 bg-white/5 rounded-lg">{icon}</div>
        <div>
          <h4 className="font-bold text-white text-sm">{title}</h4>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
      </div>
      <div className="text-right">
        <span className={`block font-bold ${highlight ? 'text-green-400' : 'text-white'}`}>{cost}</span>
      </div>
    </div>
  )
}

function PhoneMockup({
  src,
  frames,
  alt,
  onClick,
  urlLabel
}: {
  src?: string
  frames?: { key: string; src: string; title: string; status: string }[]
  alt: string
  onClick?: () => void
  urlLabel?: string
}) {
  const sequence = frames && frames.length > 0 ? frames : (src ? [{ key: 'single', src, title: 'Overpixel', status: 'Abrindo…' }] : [])
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (sequence.length <= 1) return
    const id = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % sequence.length)
    }, 2200)
    return () => window.clearInterval(id)
  }, [sequence.length])

  const active = sequence[Math.min(activeIndex, Math.max(0, sequence.length - 1))]
  const batteryLevel = 92 - (activeIndex % 5) * 3

  return (
    <div className="w-full flex flex-col items-center">
      <button
        type="button"
        onClick={onClick}
        className="group relative w-[280px] sm:w-[320px] md:w-[350px] aspect-[10/17] rounded-[2.8rem] bg-gradient-to-b from-slate-800/70 to-slate-950/80 border border-white/15 shadow-2xl shadow-cyan-900/25 overflow-hidden transition-transform hover:-translate-y-1"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-white/15 via-transparent to-transparent opacity-70" />
        <div className="absolute left-0 right-0 top-2 flex justify-center pointer-events-none z-20">
          <div className="w-[112px] h-[30px] rounded-full bg-black/90 border border-white/10 shadow-inner flex items-center justify-center gap-2">
            <div className="w-2 h-2 rounded-full bg-slate-500" />
            <div className="w-2 h-2 rounded-full bg-slate-700" />
          </div>
        </div>
        <div className="absolute left-[1px] top-24 w-[4px] h-10 rounded-r bg-white/20" />
        <div className="absolute left-[1px] top-40 w-[4px] h-16 rounded-r bg-white/20" />
        <div className="absolute right-[1px] top-32 w-[4px] h-20 rounded-l bg-white/20" />

        <div className="relative h-full w-full p-[7px]">
          <div className="relative h-full w-full rounded-[2.35rem] overflow-hidden bg-black border border-white/10">
            <AnimatePresence mode="wait">
              <motion.img
                key={active?.key || 'empty'}
                src={active?.src}
                alt={alt}
                className="h-full w-full object-contain bg-black"
                initial={{ opacity: 0.0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0.0, scale: 0.99 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
              />
            </AnimatePresence>

            <div className="absolute top-3 left-4 right-4 flex items-center justify-between z-10">
              <div className="text-[11px] font-semibold text-white/90">9:41</div>
              <div className="flex items-center gap-2">
                <div className="flex items-end gap-0.5">
                  <div className="w-1 h-1.5 rounded bg-white/70" />
                  <div className="w-1 h-2 rounded bg-white/70" />
                  <div className="w-1 h-2.5 rounded bg-white/80" />
                  <div className="w-1 h-3 rounded bg-white" />
                </div>
                <div className="relative w-6 h-3 rounded-sm border border-white/80">
                  <div
                    className="absolute left-0.5 top-0.5 bottom-0.5 rounded-[2px] bg-green-400"
                    style={{ width: `${Math.max(10, batteryLevel)}%` }}
                  />
                  <div className="absolute -right-1 top-[3px] w-[2px] h-[6px] rounded-r bg-white/80" />
                </div>
                <div className="text-[10px] font-bold text-white/90">{batteryLevel}%</div>
              </div>
            </div>
          </div>
        </div>
      </button>

      {urlLabel && (
        <div className="mt-3 text-xs font-mono text-gray-500">
          {urlLabel}
        </div>
      )}
      <div className="mt-1 text-xs text-gray-400">
        Clique para abrir no navegador
      </div>
    </div>
  )
}

function DesktopMonitorMockup({ src, alt }: { src: string; alt: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-120px" }}
      className="w-full"
    >
      <div className="relative mx-auto w-full max-w-[720px]">
        <div className="relative rounded-[2rem] bg-gradient-to-b from-slate-800/70 to-slate-950/85 border border-white/15 shadow-2xl shadow-cyan-900/20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-white/12 via-transparent to-transparent" />
          <div className="p-3">
            <div className="relative rounded-[1.6rem] overflow-hidden bg-black border border-white/10">
              <div className="aspect-video">
                <img src={src} alt={alt} className="h-full w-full object-cover" />
              </div>
              <div className="absolute inset-0 ring-1 ring-white/5" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
            </div>
          </div>
        </div>

        <div className="mx-auto mt-4 h-5 w-44 rounded-b-2xl bg-gradient-to-b from-slate-700/60 to-slate-950/90 border border-white/10" />
        <div className="mx-auto -mt-1 h-3 w-24 rounded-b-xl bg-black/70 border border-white/10" />
      </div>
    </motion.div>
  )
}

function TestimonialCard({ text, author, role, initials }: any) {
  return (
    <div className="p-8 rounded-2xl bg-white/5 border border-white/10 hover:border-white/20 transition-colors flex flex-col h-full">
      <div className="flex gap-1 text-yellow-500 mb-6">
        <Star className="w-4 h-4 fill-current" />
        <Star className="w-4 h-4 fill-current" />
        <Star className="w-4 h-4 fill-current" />
        <Star className="w-4 h-4 fill-current" />
        <Star className="w-4 h-4 fill-current" />
      </div>
      <p className="text-gray-300 italic mb-8 flex-1">"{text}"</p>
      <div className="flex items-center gap-4 mt-auto">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
          {initials}
        </div>
        <div>
          <h4 className="font-bold text-white text-sm">{author}</h4>
          <p className="text-xs text-gray-500">{role}</p>
        </div>
      </div>
    </div>
  )
}
